import type { TeleconsultManagementVisit } from '../../types/api';

/** Reason chip presets for the Patient Log (AND with other filters). */
export type ReasonPreset = 'any' | 'payment_timeout' | 'has_cancel' | 'has_refund';

export type LogFilterState = {
  search: string;
  /** Empty = all staff. Multi-select of registrationStaffName (or "System"). */
  staffNames: string[];
  /** Empty = all. Match if visit.documentLabels intersects. */
  documentLabels: string[];
  reasonPreset: ReasonPreset;
};

export const EMPTY_LOG_FILTERS: LogFilterState = {
  search: '',
  staffNames: [],
  documentLabels: [],
  reasonPreset: 'any',
};

export const REASON_PRESET_OPTIONS: { value: ReasonPreset; label: string }[] = [
  { value: 'any', label: 'Any reason' },
  { value: 'payment_timeout', label: 'Payment timeout' },
  { value: 'has_cancel', label: 'Has cancel reason' },
  { value: 'has_refund', label: 'Has refund reason' },
];

export function staffDisplayName(v: TeleconsultManagementVisit): string {
  return (v.registrationStaffName || '').trim() || 'System';
}

export function distinctStaffNames(visits: TeleconsultManagementVisit[]): string[] {
  return Array.from(new Set(visits.map(staffDisplayName))).sort((a, b) => a.localeCompare(b));
}

export function distinctDocumentLabels(visits: TeleconsultManagementVisit[]): string[] {
  const set = new Set<string>();
  visits.forEach((v) => (v.documentLabels || []).forEach((label) => {
    const t = (label || '').trim();
    if (t) set.add(t);
  }));
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function statusSearchText(status: string): string {
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'refunded') return 'refunded';
  if (status === 'open') return 'open';
  return (status || '').toLowerCase();
}

/** Free-text match across name, NRIC, staff, doctor, reason, docs, type, status, handling step. */
export function visitMatchesSearch(v: TeleconsultManagementVisit, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;

  const haystacks: string[] = [
    v.patientName || '',
    v.maskedNric || '',
    staffDisplayName(v),
    v.handlingStaffStep || '',
    v.doctorName || '',
    v.type || '',
    statusSearchText(v.status || ''),
    v.cancellationReason || '',
    v.refundReason || '',
    ...(v.documentLabels || []),
  ];

  return haystacks.some((h) => h.toLowerCase().includes(q));
}

export function visitMatchesStaffFilter(v: TeleconsultManagementVisit, staffNames: string[]): boolean {
  if (!staffNames.length) return true;
  return staffNames.includes(staffDisplayName(v));
}

export function visitMatchesDocFilter(v: TeleconsultManagementVisit, documentLabels: string[]): boolean {
  if (!documentLabels.length) return true;
  const labels = (v.documentLabels || []).map((x) => x.trim().toLowerCase());
  return documentLabels.some((want) => labels.includes(want.trim().toLowerCase()));
}

export function visitMatchesReasonPreset(v: TeleconsultManagementVisit, preset: ReasonPreset): boolean {
  if (preset === 'any') return true;
  const cancel = (v.cancellationReason || '').trim();
  const refund = (v.refundReason || '').trim();
  if (preset === 'payment_timeout') {
    return /payment not completed in time/i.test(cancel);
  }
  if (preset === 'has_cancel') return cancel.length > 0;
  if (preset === 'has_refund') return refund.length > 0;
  return true;
}

export function visitMatchesLogFilters(v: TeleconsultManagementVisit, filters: LogFilterState): boolean {
  return (
    visitMatchesSearch(v, filters.search)
    && visitMatchesStaffFilter(v, filters.staffNames)
    && visitMatchesDocFilter(v, filters.documentLabels)
    && visitMatchesReasonPreset(v, filters.reasonPreset)
  );
}

export function hasActiveLogFilters(filters: LogFilterState): boolean {
  return (
    filters.search.trim().length > 0
    || filters.staffNames.length > 0
    || filters.documentLabels.length > 0
    || filters.reasonPreset !== 'any'
  );
}

/** Newest visitDate / registeredAt first; PatientName as final tie-break (matches API). */
export function sortVisitsNewestFirst(visits: TeleconsultManagementVisit[]): TeleconsultManagementVisit[] {
  return [...visits].sort((a, b) => {
    const dayA = String(a.visitDate || '').slice(0, 10);
    const dayB = String(b.visitDate || '').slice(0, 10);
    if (dayA !== dayB) return dayB.localeCompare(dayA);

    const regA = a.registeredAt ? Date.parse(a.registeredAt) : Number.NEGATIVE_INFINITY;
    const regB = b.registeredAt ? Date.parse(b.registeredAt) : Number.NEGATIVE_INFINITY;
    if (regA !== regB) return regB - regA;

    return (a.patientName || '').localeCompare(b.patientName || '');
  });
}

export function filterAndSortLogVisits(
  visits: TeleconsultManagementVisit[],
  filters: LogFilterState,
): TeleconsultManagementVisit[] {
  return sortVisitsNewestFirst(visits.filter((v) => visitMatchesLogFilters(v, filters)));
}
